/**
 * <DynamicCustomFields formKey="..." values onChange />
 *
 * Renders ONLY the tenant-defined custom fields for a given form_key.
 * Drop this component below your form's hard-coded system fields and
 * the rest "just works" — schemas are fetched live from the backend
 * via /api/forms/{form_key}/schema and reflect whatever a Holding
 * Admin (or Maker) defined for this tenant.
 *
 * Usage:
 *   const [extras, setExtras] = useState({});
 *   ...
 *   <DynamicCustomFields formKey="entity" values={extras} onChange={setExtras} />
 *
 *   // On submit, merge `extras` into your payload under `custom_fields`.
 *
 * Props:
 *   - formKey:   string — required, must match a registered backend form_key.
 *   - values:    object — current { field_key: value } map.
 *   - onChange:  (next) => void — receives the next values map.
 *   - className: optional Tailwind extras for the outer container.
 *   - inline:    bool — if true, skip the "Custom fields" section header
 *                       (useful when you want to flow inline with system fields).
 */
import React, { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { Loader2, Sparkles, Info } from 'lucide-react';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the schema fetch effect. */

function FieldLabel({ field }) {
  return (
    <label
      htmlFor={`cf-${field.field_key}`}
      className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 flex items-center gap-1.5"
    >
      {field.label}
      {field.required && <span className="text-rose-500" aria-label="required">*</span>}
    </label>
  );
}

function FieldHelp({ field }) {
  if (!field.help_text) return null;
  return (
    <div className="flex items-start gap-1 text-[11px] text-slate-500 dark:text-white/55 mt-1">
      <Info size={11} className="mt-0.5 shrink-0" />
      <span>{field.help_text}</span>
    </div>
  );
}

const INPUT_CLASS =
  'w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 ' +
  'bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm ' +
  'focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20';

function renderInput(field, value, setValue) {
  const common = {
    id: `cf-${field.field_key}`,
    name: field.field_key,
    'data-testid': `custom-field-${field.field_key}`,
    required: !!field.required,
    placeholder: field.placeholder || '',
    className: INPUT_CLASS,
  };

  switch (field.type) {
    case 'textarea':
      return (
        <textarea
          {...common}
          rows={3}
          value={value ?? ''}
          onChange={(e) => setValue(e.target.value)}
        />
      );
    case 'number':
      return (
        <input
          {...common}
          type="number"
          value={value ?? ''}
          onChange={(e) => setValue(e.target.value === '' ? '' : Number(e.target.value))}
        />
      );
    case 'date':
      return (
        <input
          {...common}
          type="date"
          value={value ?? ''}
          onChange={(e) => setValue(e.target.value)}
        />
      );
    case 'email':
      return (
        <input
          {...common}
          type="email"
          value={value ?? ''}
          onChange={(e) => setValue(e.target.value)}
        />
      );
    case 'url':
      return (
        <input
          {...common}
          type="url"
          value={value ?? ''}
          onChange={(e) => setValue(e.target.value)}
        />
      );
    case 'phone':
      return (
        <input
          {...common}
          type="tel"
          value={value ?? ''}
          onChange={(e) => setValue(e.target.value)}
        />
      );
    case 'checkbox':
      return (
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            id={common.id}
            data-testid={common['data-testid']}
            type="checkbox"
            checked={!!value}
            onChange={(e) => setValue(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
          />
          <span className="text-sm text-slate-700 dark:text-white/85">
            {field.placeholder || `Confirm ${field.label.toLowerCase()}`}
          </span>
        </label>
      );
    case 'select':
      return (
        <select
          {...common}
          value={value ?? ''}
          onChange={(e) => setValue(e.target.value)}
        >
          <option value="">— Select —</option>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    case 'multiselect': {
      const current = Array.isArray(value) ? value : [];
      const toggle = (v) =>
        setValue(current.includes(v) ? current.filter((x) => x !== v) : [...current, v]);
      return (
        <div className="flex flex-wrap gap-2" data-testid={common['data-testid']}>
          {(field.options || []).map((opt) => {
            const active = current.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  active
                    ? 'bg-violet-600 text-white border-violet-700'
                    : 'bg-white dark:bg-zinc-900 text-slate-700 dark:text-white/75 border-slate-300 dark:border-white/15 hover:border-violet-400'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      );
    }
    case 'text':
    default:
      return (
        <input
          {...common}
          type="text"
          value={value ?? ''}
          onChange={(e) => setValue(e.target.value)}
        />
      );
  }
}

export default function DynamicCustomFields({
  formKey,
  values = {},
  onChange,
  className = '',
  inline = false,
  showSystemFields = false,
}) {
  const [schema, setSchema] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/forms/${formKey}/schema`)
      .then(({ data }) => { if (alive) setSchema(data); })
      .catch((err) => {
        if (!alive) return;
        setError(err?.response?.data?.detail || err.message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [formKey]);

  const renderable = useMemo(() => {
    if (!schema) return [];
    return showSystemFields
      ? schema.fields
      : schema.fields.filter((f) => !f.is_system);
  }, [schema, showSystemFields]);

  const setVal = (key, v) => {
    onChange({ ...(values || {}), [key]: v });
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-xs text-slate-500 dark:text-white/55 ${className}`} data-testid={`cf-loading-${formKey}`}>
        <Loader2 size={12} className="animate-spin" /> Loading custom fields…
      </div>
    );
  }
  if (error) {
    return (
      <div className={`text-xs text-rose-600 dark:text-rose-300 ${className}`} data-testid={`cf-error-${formKey}`}>
        Couldn&apos;t load custom fields: {error}
      </div>
    );
  }
  if (renderable.length === 0) {
    return null; // nothing custom defined yet — silent
  }

  return (
    <div className={className} data-testid={`custom-fields-${formKey}`}>
      {!inline && (
        <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/55">
          <Sparkles size={12} />
          Custom fields
        </div>
      )}
      <div className="space-y-4">
        {renderable.map((field) => (
          <div key={field.field_id} data-testid={`cf-${field.field_key}`}>
            {field.type !== 'checkbox' && <FieldLabel field={field} />}
            {renderInput(field, values?.[field.field_key], (v) => setVal(field.field_key, v))}
            <FieldHelp field={field} />
          </div>
        ))}
      </div>
    </div>
  );
}
